import { DuplicateAsyncValidators } from '../validators/duplicate-async.validators'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core'
import { UserService } from '../services/user.service'
import { FormService } from '../services/form.service'
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms'
import { EMPTY, empty, iif } from 'rxjs'
import { SegmentService } from '../analytics/services/segment.service'
import { SegmentEvents } from '../analytics/models/segment-events.model'
import { VerticalService } from '../../singleton-services/vertical.service'
import { validateEmail } from '../validators/email.validator'
import { flatMap, map, take } from 'rxjs/operators'
import { LoggerService } from '../../logger/logger.interface'
import { LOGGER_SERVICE } from '../../logger/logger.config'

@Component({
  selector: 'flo-newsletter-widget',
  styleUrls: ['./newsletter-widget.component.scss'],
  templateUrl: './newsletter-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterWidgetComponent {
  public sportName$ = this.verticalService.siteSettings$.pipe(
    map(settings => {
      return settings.sport_name
    })
  )
  public siteName$ = this.verticalService.siteSettings$.pipe(
    map(settings => {
      return settings.site_name
    })
  )

  public newsletterFormView = true
  public newsletterFormSubmitting = false
  public newsletterForm = this.fb.group({
    email: [
      '',
      [Validators.required, Validators.minLength(6), Validators.maxLength(75), validateEmail],
      [this.duplicateAsyncValidators.validateEmailDoesNotExist(false)]
    ]
  })

  public get email() {
    return this.newsletterForm.get('email')
  }

  public emailSpinner$ = iif(
    () => this.email !== null,
    (this.email as AbstractControl).statusChanges.pipe(map(status => status === 'PENDING', empty())),
    EMPTY
  )

  constructor(
    private fb: UntypedFormBuilder,
    private changeRef: ChangeDetectorRef,
    private formService: FormService,
    @Inject(LOGGER_SERVICE) private readonly loggerService: LoggerService,
    private duplicateAsyncValidators: DuplicateAsyncValidators,
    private segmentService: SegmentService,
    private verticalService: VerticalService,
    private userService: UserService
  ) {}

  public showErrorBoxMessage(fieldName: string): boolean {
    return this.formService.showErrorMessage(this.newsletterForm, fieldName)
  }

  public submitForm(): void {
    if (!this.newsletterForm.valid || this.newsletterFormSubmitting) return

    const email = this.newsletterForm.value.email
    this.newsletterFormSubmitting = true

    this.segmentService.track(SegmentEvents.FORM_SUBMITTED, {
      name: 'Newsletter Subscription'
    })

    this.verticalService.siteId$
      .pipe(
        flatMap(siteId => this.userService.signupForNewsletter(email, siteId)),
        take(1)
      )
      .subscribe(
        res => {
          this.newsletterFormView = false
          this.newsletterFormSubmitting = false
          this.segmentService.track(SegmentEvents.NEWSLETTER_SUBSCRIBED, {
            email
          })
          this.changeRef.markForCheck()
        },
        err => {
          this.loggerService.error('Error signing up for newsletter', { err })
          if (!this.email) return
          this.email.setErrors({ server_error: true })
          this.newsletterFormSubmitting = false
          this.changeRef.markForCheck()
        }
      )
  }

  public sendNewsletterDisplayed() {
    this.segmentService.track(SegmentEvents.NEWSLETTER_WIDGET_DISPLAYED, {})
  }

  public sendClickEvent() {
    this.segmentService.track(SegmentEvents.INPUT_FIELD_CLICKED, {
      name: 'Email'
    })
  }

  public readonly termsOfServiceURL = 'https://www.flosports.tv/terms-of-service/'
  public readonly privacyPolicyURL = 'https://www.flosports.tv/privacy-policy/'
}
